import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Redirect } from 'react-router'
import Popup from 'react-popup'
import ScrollbarSize from 'react-scrollbar-size'
import { fetchAndRefresh } from '@teqplay/web-sdk/rest'
import { Slide, ToastContainer } from 'react-toastify'
import { isEqual } from 'lodash'

import 'react-toastify/dist/ReactToastify.css'

import Prompt from '../prompt/Prompt'
import SpinnerOverlay from '../spinnerOverlay/SpinnerOverlay'
import Routes from './routes/Routes'
import Menu from '../menu/Menu'
import Modal from './customModal/Modal'
import TerminalVisitForm from '../terminalVisitForm/TerminalVisitForm'
import TerminalMaintenanceEdit from '../terminalMaintenance/TerminalMaintenanceEdit'
import TerminalMaintenanceDisplay from '../terminalMaintenance/TerminalMaintenanceDisplay'
import {
  fetchConfigAndTerminalContent,
  insertTerminalMaintenanceInBackend,
  insertTerminalVisitInBackend,
  setSelectedMaintenance,
  setSelectedVisit,
  setTerminalMaintenance,
  setTerminalVisits,
  updateTerminalMaintenanceInBackend,
  updateTerminalVisitInBackend
} from './actions'
import { clearAuth, setNewToken, switchTerminal } from '../../pages/login/actions'
import { setMenuDropdownExpanded, setScrollBarWidth } from '../ui/actions'
import {
  deleteTerminalMaintenanseFromBackendRest,
  deleteTerminalVisitFromBackendRest,
  fetchShipDetailsFromBackend,
  updateTerminalVisitInBackendRest
} from '../../js/rest'
import { copyTerminalVisitAndReplaceOrders } from '../../utils/general'
import { BASE_URL } from '../../js/constants'
import './App.scss'
import UploadFile from '../uploadFile/UploadFile'
import { getTerminalStatus } from '../../js/migration'
import { getDataRange } from '../../utils/terminalVisitUtils'
import { handleFetchError } from '../../js/sentry'
import ErrorBoundary from '../errorBoundary/ErrorBoundary'
import { initializeAnalyticsTracking, reportUserEvent, USER_EVENTS } from '../../utils/analyticsUtils'
import { userPermissionRoles } from '../../utils/userPermissions'

import '@fortawesome/fontawesome-free/css/all.css'

/** Prompt plugin */
Popup.registerPlugin('prompt', function (defaultValue, title, placeholder, callback) {
  let promptValue = null
  const promptChange = function (value) {
    promptValue = value
  }

  this.create({
    title: title,
    content: <Prompt onChange={promptChange} placeholder={placeholder} value={defaultValue} />,
    buttons: {
      right: [
        {
          text: 'Save',
          key: '⌘+s',
          className: 'yes-btn btn',
          action: () => {
            if (promptValue) {
              callback(promptValue)
              Popup.close()
            }
          }
        }
      ]
    }
  })
})

class App extends Component {
  constructor(props) {
    super(props)

    this.state = {
      loadingConfig: true,
      showCargoManifestTemplateModal: false
    }
  }

  componentDidUpdate(prevProps) {
    // When user presses redo or undo button
    if (
      this.props.app.future.length < prevProps.app.future.length ||
      this.props.app.past.length < prevProps.app.past.length
    ) {
      this.handleUndoAndRedoVisitUpdate(
        prevProps.app.present.terminalVisits,
        this.props.app.present.terminalVisits,
        this.props.login.auth.token,
        this.fetch
      )
    }
  }

  componentDidMount() {
    if (this.isLoggedIn()) {
      this.inititalizeApp()
    }

    const username = this.props.login.auth.username
    initializeAnalyticsTracking(username)
  }

  /**
   * Dispatches a redux action to switch the user's terminal
   * @param{string} newTerminal ID for new terminal
   * @param{[]} userRoles array containing user's roles on new terminal
   */
  handleSwitchTerminal = async (newTerminal, userRoles) => {
    await this.props.dispatch(switchTerminal(newTerminal, userRoles))
    this.inititalizeApp()

    reportUserEvent(USER_EVENTS.TERMINAL_SWITCH)
  }

  render() {
    if (
      !this.isLoggedIn() ||
      (!this.props.app.present.config && !this.state.loadingConfig) ||
      !this.props.login.userTerminals ||
      !this.props.login.auth.userSettings
    ) {
      return (
        <Redirect
          to={{
            pathname: '/login',
            state: { previousPath: this.props.location.pathname }
          }}
          push
        />
      )
    }

    // Wait for the config to load, then continue
    if (this.state.loadingConfig) {
      return (
        <div className="loading-login-wrapper">
          <ToastContainer autoClose={5000} transition={Slide} />
          <SpinnerOverlay loading />
        </div>
      )
    }

    const { planner: isPlanner } = userPermissionRoles(this.props.login.auth.roles)

    const selectedVisit = this.props.app.present.selectedVisit
    let title = 'Terminal visit'
    if (selectedVisit) {
      if (selectedVisit.ship && selectedVisit.ship.name) {
        title += ' - ' + selectedVisit.ship.name
      } else if (selectedVisit.ship && selectedVisit.ship.length) {
        title += ' - ' + selectedVisit.ship.length + ' m'
      }
    }

    return (
      <ErrorBoundary>
        <div className="App" onClick={this.mainAppClickHandler}>
          <ToastContainer autoClose={5000} transition={Slide} />

          <Popup />

          <Menu
            customFetch={this.fetch}
            onShowCargoManifest={() => {
              this.handleShowCargoManifestModal(true)
            }}
            onSelectTerminal={this.handleSwitchTerminal}
          />

          {this.state.showCargoManifestTemplateModal ? (
            <Modal.Wrapper
              title={'Upload Cargo Manifest Template'}
              className="upload-excel"
              onCloseModal={() => this.handleShowCargoManifestModal(false)}
              isOpen
            >
              <UploadFile
                url={`${BASE_URL}/v1/terminal/${this.props.login.auth.terminalId}/cargoManifestTemplate`}
                description={
                  <span>
                    The template must be a Microsoft Word document (.docx) containing place holders like{' '}
                    <code>{'{{SHIP_NAME}}'}</code> which will be filled in when downloading a Cargo Manifest for a
                    specific terminal visit.
                  </span>
                }
                formField="file"
                fileType=".docx"
                onClose={() => this.handleShowCargoManifestModal(false)}
                authToken={this.props.login.auth.token}
                customFetch={this.fetch}
              />
            </Modal.Wrapper>
          ) : null}

          {this.props.app.present.selectedVisit ? (
            <Modal.Wrapper
              title={title}
              className="flex columns"
              onCloseModal={() => this.props.dispatch(setSelectedVisit(undefined, undefined))}
              isOpen
            >
              <TerminalVisitForm
                // FIXME: support multiple quays?
                quay={
                  this.props.app.present.config.quays.length < 2 ? this.props.app.present.config.quays[0].name : null
                }
                selectedVisit={this.props.app.present.selectedVisit}
                selectedOrderId={this.props.app.present.selectedOrderId}
                onSelectVisit={this.handleSelectVisit}
                updateTerminalVisit={this.handleUpdateTerminalVisit}
                copyTerminalVisit={this.handleCopyTerminalVisit}
                getShipDetailsByMmsi={this.getShipDetailsByMmsi}
                deleteTerminalVisit={this.deleteTerminalVisit}
                config={this.props.app.present.config}
                insertTerminalVisit={this.insertTerminalVisit}
                terminalVisits={this.props.app.present.terminalVisits}
                authToken={this.props.login.auth.token}
                customFetch={this.fetch}
                userRoles={this.props.login.auth.roles}
              />
            </Modal.Wrapper>
          ) : null}

          {this.props.app.present.selectedMaintenance ? (
            <Modal.Wrapper
              title={'Terminal maintenance'}
              className="terminal-maintenance-modal"
              onCloseModal={() => this.props.dispatch(setSelectedMaintenance(null))}
              isOpen
            >
              {isPlanner ? (
                <TerminalMaintenanceEdit
                  terminalId={this.props.login.auth.terminalId}
                  quay={this.props.app.present.config.quays[0].name}
                  selectedMaintenance={this.props.app.present.selectedMaintenance}
                  config={this.props.app.present.config}
                  onInsertTerminalMaintenance={this.handleInsertTerminalMaintenance}
                  onUpdateTerminalMaintenance={this.handleUpdateTerminalMaintenance}
                  onCancelTerminalMaintenance={this.handleCancelTerminalMaintenance}
                />
              ) : (
                <TerminalMaintenanceDisplay selectedMaintenance={this.props.app.present.selectedMaintenance} />
              )}
            </Modal.Wrapper>
          ) : null}

          <Routes customFetch={this.fetch} roles={this.props.login.auth.roles} />

          <div className="bugfixes">
            <a href="https://example.com" id="downloadTrigger">
              downloadtrigger
            </a>
            <ScrollbarSize onLoad={this.scrollbarUpdate} onChange={this.scrollbarUpdate} />
          </div>
        </div>
      </ErrorBoundary>
    )
  }

  /**
   * Returns true if user is logged in
   */
  isLoggedIn = () => !!(this.props.login.auth.token && this.props.login.auth.username && this.props.login.auth.roles) // auth.roles is added recently to the persisted state, need to check explicitly for it for now. A few weeks after deployment we can remove this check again

  inititalizeApp() {
    const { from, to } = getDataRange(this.props.history, this.props.app.present.dataRange)
    this.props.dispatch(
      fetchConfigAndTerminalContent(
        this.props.login.auth.terminalId,
        from,
        to,
        this.props.login.auth.token,
        this.fetch,
        this.finishFetchConfig
      )
    )
  }

  finishFetchConfig = () => {
    this.setState({ loadingConfig: false })
  }

  /**
   * Handle the backend updating of visits that are changed through undo / redo button actions
   * @param prevVisits
   * @param currentVisits
   * @param authToken
   * @param customFetch
   */
  handleUndoAndRedoVisitUpdate = (prevVisits, currentVisits, authToken, customFetch) => {
    // Get visits that are changed through the undo action
    const updatedVisits = this.getChangedVisitsSincePreviousState(prevVisits, currentVisits)

    // Update the visit (ONLY in the backend, frontend is already updated by undo/redo action)
    updatedVisits.forEach(visit => updateTerminalVisitInBackendRest(visit, authToken, customFetch))
  }

  /**
   * Get visits that are changed since previous props/state
   * (just supply two arrays and this function will return the visits with the same id that have different
   * properties / values)
   * @param prevVisits
   * @param currentVisits
   * @returns {Array}
   */
  getChangedVisitsSincePreviousState = (prevVisits, currentVisits) => {
    const changedVisits = []

    prevVisits.forEach(prevVisit => {
      currentVisits.forEach(currentVisit => {
        if (prevVisit._id === currentVisit._id && !isEqual(prevVisit, currentVisit)) {
          changedVisits.push(currentVisit)
        }
      })
    })

    return changedVisits
  }

  /**
   * Click handler when user clicks anywhere in the app outside any buttons or other clickhandling elements
   */
  mainAppClickHandler = () => {
    if (this.props.ui.menuDropdownExpanded) {
      this.props.dispatch(setMenuDropdownExpanded(false))
    }
  }

  scrollbarUpdate = measurements => this.props.dispatch(setScrollBarWidth(measurements.scrollbarWidth))

  /**
   * @param {TerminalVisit} selectedVisit
   * @param {string} selectedOrderId
   */
  handleSelectVisit = (selectedVisit, selectedOrderId) => {
    this.props.dispatch(setSelectedVisit(selectedVisit, selectedOrderId))
  }

  /**
   * Update/replace terminal visit in backend AND in local store with given terminalVisit
   * @param terminalVisit
   */
  handleUpdateTerminalVisit = terminalVisit => {
    this.props.dispatch(setSelectedVisit(undefined, undefined))

    this.props.dispatch(updateTerminalVisitInBackend(terminalVisit, this.props.login.auth.token, this.fetch))
  }

  handleCopyTerminalVisit = (visit, order) => {
    const visitCopy = copyTerminalVisitAndReplaceOrders(visit, order, this.props.app.present.config)

    if (visitCopy.parallelGroups > 1) {
      visitCopy.orders[0].parallelGroup = 0
      visitCopy.parallelGroups = 1
    }

    this.props.dispatch(setSelectedVisit(visitCopy, undefined))
  }

  /**
   * Delete a terminalVisit from the backend
   * @param visitId
   */
  deleteTerminalVisit = async visitId => {
    try {
      await deleteTerminalVisitFromBackendRest(visitId, this.props.login.auth.token, this.fetch)

      const terminalVisits = this.props.app.present.terminalVisits.filter(visit => visit._id !== visitId)
      this.props.dispatch(setTerminalVisits(terminalVisits))
      this.props.dispatch(setSelectedVisit(undefined, undefined))
    } catch (err) {
      handleFetchError(err)
    }
  }

  /**
   * Insert a new terminalVisit into the backend
   * @param visit
   */
  insertTerminalVisit = visit => {
    this.props.dispatch(
      insertTerminalVisitInBackend(visit, this.props.app.present.config, this.props.login.auth.token, this.fetch)
    )
    this.props.dispatch(setSelectedVisit(undefined, undefined))
  }

  /**
   * Get ship details from backend by mmsi
   * @param mmsi
   * @returns {*}
   */
  getShipDetailsByMmsi = mmsi => fetchShipDetailsFromBackend(mmsi, this.props.login.auth.token, this.fetch)

  /**
   * Insert a new TerminalMaintenance into the backend
   * @param maintenance
   */
  handleInsertTerminalMaintenance = maintenance => {
    this.props.dispatch(
      insertTerminalMaintenanceInBackend(
        maintenance,
        this.props.app.present.terminalMaintenance,
        this.props.login.auth.token,
        this.fetch
      )
    )
    this.props.dispatch(setSelectedMaintenance(null))
  }

  /**
   * Update/replace terminal maintenance in backend AND in local store with given terminal maintenance
   * @param terminalMaintenance
   */
  handleUpdateTerminalMaintenance = terminalMaintenance => {
    this.props.dispatch(
      updateTerminalMaintenanceInBackend(
        terminalMaintenance,
        this.props.app.present.terminalMaintenance,
        this.props.login.auth.token,
        this.fetch
      )
    )
    this.props.dispatch(setSelectedMaintenance(null))
  }

  /**
   * Cancel / remove a terminalMaintenance from the backend
   * @param maintenanceId
   */
  handleCancelTerminalMaintenance = maintenanceId => {
    const terminalMaintenance = this.props.app.present.terminalMaintenance.filter(
      maintenance => maintenance._id !== maintenanceId
    )

    this.deleteTerminalMaintenanseFromBackend(maintenanceId, this.props.login.auth.token, this.fetch)
    this.props.dispatch(setTerminalMaintenance(terminalMaintenance))
    this.props.dispatch(setSelectedMaintenance(null))
  }

  /**
   * Updates terminal maintenance
   * @param {string} terminalMaintenanseId
   * @param {string} authToken
   * @param {function} customFetch
   */
  deleteTerminalMaintenanseFromBackend = async (terminalMaintenanseId, authToken, customFetch) => {
    try {
      await deleteTerminalMaintenanseFromBackendRest(terminalMaintenanseId, authToken, customFetch)
    } catch (err) {
      handleFetchError(err)
    }
  }

  handleShowCargoManifestModal = show => {
    this.setState({ showCargoManifestTemplateModal: show })
  }

  /**
   * Project wide function which all fetches are guided through
   * This makes sure that if a fetch gets a 401 returned that it will be processed accordingly
   * @param {String} url      The url of the original fetch, with all parameters already added
   * @param {Object} options  The options of the original fetch you want to execute, with a reference to a token inside
   */
  fetch = async (url, options, customResolver) => {
    try {
      return await fetchAndRefresh({
        url,
        options,
        projectURL: BASE_URL + '/v1',
        auth: this.props.login.auth,
        onChangeAuth: auth => this.props.dispatch(setNewToken(auth.token, auth.refreshToken)),
        customResolver
      })
    } catch (err) {
      // FIXME: this is a hacky fix to hide cargo operations endpoint returning a 404 when it doesn't exist. Solve that in a more neat way
      if (err.toString().indexOf('Cargo operations details not found') !== -1) {
        return err
      }

      console.error('fetch error', err)
      if (err.status === 401) {
        this.props.dispatch(clearAuth())
      }
      // toast.error(err.toString(), { autoClose: 60000 })
      return err
    }
  }

  checkMaintenanceMode = async () => {
    const { port, terminal, username } = this.props.login.auth

    if (!port || !terminal) {
      return
    }

    console.info(`Checking terminal status for port ${port}, terminal ${terminal}...`)
    const status = await getTerminalStatus(port, terminal, username)
    console.info('Terminal status: ' + status)

    if (status === 'MAINTENANCE') {
      console.info('Terminal status changed to MAINTENANCE, logging out now.')

      this.props.dispatch(clearAuth())
    }
  }
}

export default connect(state => state)(App)
